import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { MessageSquare, Send, RefreshCw, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { format, parseISO } from 'date-fns';
import {
  AttachMenu, PendingAttachments, AttachmentList,
  filterNewFiles, uploadAttachments, useFileDrop,
} from './MessageAttachments';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shared admin<->employee message thread.
// viewer='admin' needs employeeId; viewer='employee' uses the /users/me endpoints.
export const StaffMessagesThread = ({ viewer, employeeId, department = null, channel = null }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const baseUrl = viewer === 'admin'
    ? `${API}/admin/employees/${employeeId}/messages`
    : `${API}/users/me/messages`;

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = viewer === 'admin'
        ? (department ? { department } : {})
        : (channel && channel !== 'admin' ? { channel } : {});
      const res = await axios.get(baseUrl, { params });
      setMessages(res.data || []);
    } catch {
      if (!silent) toast.error('Failed to load messages');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [baseUrl, department, channel, viewer]);

  useEffect(() => { load(); }, [load]);
  useAutoRefresh(() => load(true), { match: ['/messages'], intervalMs: 20000 });
  useEffect(() => { bottomRef.current?.scrollIntoView({ block: 'nearest' }); }, [messages.length]);

  const addFiles = useCallback((incoming) => {
    setFiles((prev) => [...prev, ...filterNewFiles(prev, incoming)]);
  }, []);
  const { dragOver, dropProps } = useFileDrop(addFiles);

  const send = async () => {
    const text = body.trim();
    if (!text && !files.length) return;
    setSending(true);
    try {
      const payload = { body: text };
      if (viewer === 'admin' && department) payload.department = department;
      if (viewer !== 'admin' && channel) payload.channel = channel;
      if (files.length) payload.attachment_ids = await uploadAttachments(files);
      const res = await axios.post(baseUrl, payload);
      setMessages((prev) => [...prev, res.data]);
      setBody('');
      setFiles([]);
      toast.success('Message sent — an email notification was also delivered');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const isMine = (m) => (viewer === 'admin' ? m.sender_role === 'admin' : m.sender_role === 'employee');

  return (
    <Card className="bg-card border-border rounded-sm" data-testid="staff-messages-thread">
      <CardHeader className="border-b border-border pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-primary" /> Messages
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={() => load(true)} data-testid="messages-refresh-btn" className="h-7 w-7" title="Refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
        {viewer === 'admin' && (
          <p className="text-xs text-muted-foreground">
            The employee also receives an email for every message you send here.
          </p>
        )}
      </CardHeader>
      <CardContent className="p-0">
        <div className="max-h-[45vh] overflow-y-auto p-4 space-y-3" data-testid="messages-list">
          {loading ? (
            <p className="text-sm text-muted-foreground text-center py-6 animate-pulse">Loading messages…</p>
          ) : messages.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6" data-testid="messages-empty">
              No messages yet — start the conversation below.
            </p>
          ) : (
            messages.map((m) => (
              <div key={m.id} className={cn('flex', isMine(m) ? 'justify-end' : 'justify-start')} data-testid={`message-${m.id}`}>
                <div className={cn(
                  'max-w-[80%] rounded-sm px-3 py-2 border',
                  isMine(m)
                    ? 'bg-primary/10 border-primary/30'
                    : 'bg-muted/40 border-border'
                )}>
                  <p className={cn('text-[10px] uppercase tracking-wider mb-1', isMine(m) ? 'text-primary' : 'text-muted-foreground')}>
                    {m.sender_name} · {(() => { try { return format(parseISO(m.created_at), 'dd MMM, HH:mm'); } catch { return ''; } })()}
                  </p>
                  {m.body && <p className="text-sm text-foreground whitespace-pre-wrap break-words">{m.body}</p>}
                  <AttachmentList attachments={m.attachments} />
                </div>
              </div>
            ))
          )}
          <div ref={bottomRef} />
        </div>
        <div className="relative border-t border-border" {...dropProps}>
          {dragOver && (
            <div className="absolute inset-0 z-10 bg-primary/10 border-2 border-dashed border-primary rounded-sm flex items-center justify-center pointer-events-none" data-testid="attach-drop-overlay">
              <p className="text-xs uppercase tracking-wider text-primary font-semibold flex items-center gap-2">
                <Paperclip className="w-4 h-4" /> Drop files to attach
              </p>
            </div>
          )}
          <PendingAttachments files={files} onRemove={(i) => setFiles((p) => p.filter((_, n) => n !== i))} testPrefix="msg" />
          <div className="p-3 flex items-end gap-2">
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write a message…"
              rows={2}
              maxLength={5000}
              data-testid="message-input"
              className="rounded-sm bg-background border-border resize-none flex-1"
            />
            <AttachMenu onFiles={addFiles} disabled={sending} testPrefix="msg" />
            <Button
              onClick={send}
              disabled={sending || (!body.trim() && !files.length)}
              data-testid="message-send-btn"
              className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm h-10"
            >
              <Send className="w-4 h-4 mr-2" /> {sending ? 'Sending…' : 'Send'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default StaffMessagesThread;
